import React from 'react'
import { Link } from 'react-router-dom'
import { useAdminProviderStats, useRecentProviders } from '../../../hooks/useProviders'
import {
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  StarIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../../components/LoadingSpinner'

function ProviderOverviewPage() {
  const { data: stats, isLoading: statsLoading } = useAdminProviderStats()
  const { data: recentProviders, isLoading: providersLoading } = useRecentProviders(5)

  if (statsLoading || providersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  const statCards = [
    {
      name: 'Total Providers',
      value: stats?.totalProviders || 0,
      icon: UsersIcon,
      color: 'bg-blue-500',
      change: stats?.totalProvidersChange || '+0%',
      changeType: 'positive' as const,
    },
    {
      name: 'Pending Approval',
      value: stats?.pendingProviders || 0,
      icon: ClockIcon,
      color: 'bg-yellow-500',
      change: stats?.pendingProvidersChange || '+0%',
      changeType: 'negative' as const,
    },
    {
      name: 'Active Providers',
      value: stats?.activeProviders || 0,
      icon: CheckCircleIcon,
      color: 'bg-green-500',
      change: stats?.activeProvidersChange || '+0%',
      changeType: 'positive' as const,
    },
    {
      name: 'Avg Rating',
      value: (stats?.averageRating || 0).toFixed(1),
      icon: StarIcon,
      color: 'bg-purple-500',
      change: stats?.averageRatingChange || '+0%',
      changeType: 'positive' as const,
      isRating: true,
    },
  ]

  const quickActions = [
    {
      name: 'Review Pending Providers',
      href: '/admin/providers/pending',
      icon: ClockIcon,
      color: 'bg-yellow-100 text-yellow-800',
      count: stats?.pendingProviders || 0,
    },
    {
      name: 'View Active Providers',
      href: '/admin/providers/active',
      icon: UsersIcon,
      color: 'bg-green-100 text-green-800',
      count: stats?.activeProviders || 0,
    },
    {
      name: 'Provider Analytics',
      href: '/admin/providers/analytics',
      icon: ChartBarIcon,
      color: 'bg-blue-100 text-blue-800',
      count: null,
    },
    {
      name: 'Provider Settings',
      href: '/admin/providers/settings',
      icon: ExclamationTriangleIcon,
      color: 'bg-gray-100 text-gray-800',
      count: null,
    },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Provider Management</h1>
            <p className="mt-1 text-sm text-neutral-600">
              Overview of all service providers and approval status
            </p>
          </div>
          <Link
            to="/admin/providers/pending"
            className="btn-primary flex items-center gap-2"
          >
            <UserPlusIcon className="h-5 w-5" />
            Review Applications
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="card">
              <div className="card-body p-6">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 p-3 rounded-lg ${stat.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-neutral-600 truncate">
                        {stat.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-neutral-900">
                          {stat.value}
                          {stat.isRating && (
                            <span className="text-sm text-neutral-500 ml-1">/5.0</span>
                          )}
                        </div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                          stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                          {stat.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-neutral-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => {
            const Icon = action.icon
            return (
              <Link
                key={action.name}
                to={action.href}
                className="card hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="card-body p-6">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-3 rounded-lg ${action.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-medium text-neutral-900">
                        {action.name}
                      </h3>
                      {action.count !== null && (
                        <p className="text-sm text-neutral-500">
                          {action.count} items
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Recent Providers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Registrations */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-neutral-900">Recent Registrations</h3>
              <Link
                to="/admin/providers/pending"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                View all
              </Link>
            </div>
            <p className="text-sm text-neutral-500">Latest provider applications</p>
          </div>
          <div className="card-body">
            {recentProviders?.length === 0 ? (
              <div className="text-center py-8">
                <UserPlusIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900">No recent registrations</h3>
                <p className="mt-1 text-sm text-neutral-500">
                  No new provider applications in the last few days
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentProviders?.map((provider) => (
                  <div key={provider.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {provider.companyName?.charAt(0) || provider.user?.firstName?.charAt(0) || 'U'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-neutral-900">
                          {provider.companyName || `${provider.user?.firstName} ${provider.user?.lastName}`}
                        </p>
                        <p className="text-sm text-neutral-500">
                          {provider.serviceType.replace('_', ' ')} • {provider.city}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        provider.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : provider.user?.isApproved
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {provider.status === 'ACTIVE' ? 'Active' : provider.user?.isApproved ? 'Pending' : 'Unapproved'}
                      </span>
                      <Link
                        to={`/admin/providers/active`}
                        className="text-neutral-400 hover:text-neutral-600"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Performing Providers */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-neutral-900">Top Performing</h3>
              <Link
                to="/admin/providers/analytics"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                View analytics
              </Link>
            </div>
            <p className="text-sm text-neutral-500">Highest rated providers this month</p>
          </div>
          <div className="card-body">
            {stats?.topProviders?.length === 0 ? (
              <div className="text-center py-8">
                <StarIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900">No performance data yet</h3>
                <p className="mt-1 text-sm text-neutral-500">
                  Start tracking provider performance as they complete bookings
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {stats?.topProviders?.slice(0, 5).map((provider: any, index: number) => (
                  <div key={provider.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <span className="text-yellow-600 font-medium text-sm text-xs">
                            #{index + 1}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-neutral-900">
                          {provider.companyName || `${provider.users?.firstName} ${provider.users?.lastName}`}
                        </p>
                        <div className="flex items-center text-sm text-neutral-500">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          {provider.rating?.toFixed(1)} • {provider._count?.bookings || 0} bookings
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-neutral-500">
                      {provider.city}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProviderOverviewPage